import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  CubeIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import {
  usePackages,
  type PackageFormData,
  type PackageItem,
  type PackageProduct,
  type Package,
} from '../../hooks/usePackages';
import { useUbicacionesStore } from '../../stores/useUbicacionesStore';
import { useAuthStore } from '../../stores/useAuthStore';

const EMPTY_FORM: PackageFormData = {
  name: '',
  description: '',
  price: '',
  image_url: '',
  is_active: true,
  items: [],
  mostrar_en_catalogo: true,
  disponible_online: true,
  destacado: false,
  tags: [],
  tiempo_preparacion: '',
  ubicacion: '',
  orden_catalogo: 0,
  mostrar_precio: true,
  mostrar_stock: true,
  imagenes: [],
  is_bookable: false,
  requires_time_slot: true,
  custom_duration_minutes: null,
  fulfillment_options: ['pickup'],
};

// Recently used product IDs in package builder (localStorage)
const RECENT_KEY = 'clientelo_paquetes_recientes';
const MAX_RECIENTES = 5;

function getRecentIds(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function saveRecentId(id: string) {
  try {
    const prev = getRecentIds().filter((i) => i !== id);
    localStorage.setItem(RECENT_KEY, JSON.stringify([id, ...prev].slice(0, MAX_RECIENTES)));
  } catch {
    // ignore
  }
}

interface PackageBuilderProps {
  editingPackage?: Package | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function PackageBuilder({
  editingPackage,
  onClose,
  onSaved,
}: PackageBuilderProps) {
  const navigate = useNavigate();
  const { currentEmpresa } = useAuthStore();
  const { products, createPackage, updatePackage, loadBookableForPackage, uploadPackageImages } = usePackages();
  const { ubicaciones, fetchUbicaciones, isLoading: ubicacionesLoading } = useUbicacionesStore();
  const [form, setForm] = useState<PackageFormData>(EMPTY_FORM);
  const [tagsInput, setTagsInput] = useState('');
  const [search, setSearch] = useState('');
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const [pendingImageFiles, setPendingImageFiles] = useState<File[]>([]);
  const [pendingPreviewUrls, setPendingPreviewUrls] = useState<string[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    if (currentEmpresa?.id) fetchUbicaciones(currentEmpresa.id);
  }, [currentEmpresa?.id, fetchUbicaciones]);

  useEffect(() => {
    if (editingPackage) {
      const base = {
        name: editingPackage.name,
        description: editingPackage.description ?? '',
        price: editingPackage.price != null ? String(editingPackage.price) : '',
        image_url: editingPackage.image_url ?? '',
        is_active: editingPackage.is_active,
        items: (editingPackage.items ?? []).map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          unit_cost_override: i.unit_cost_override ?? null,
          product: i.product,
        })),
        mostrar_en_catalogo: editingPackage.mostrar_en_catalogo === true,
        disponible_online: editingPackage.disponible_online === true,
        destacado: editingPackage.destacado === true,
        tags: (() => {
          const t: unknown = editingPackage.tags;
          if (Array.isArray(t)) return t.filter((x: unknown): x is string => typeof x === 'string');
          if (typeof t === 'string') return t.split(',').map((x) => x.trim()).filter(Boolean);
          return [];
        })(),
        tiempo_preparacion: editingPackage.tiempo_preparacion ?? '',
        ubicacion: editingPackage.ubicacion ?? '',
        orden_catalogo: editingPackage.orden_catalogo ?? 0,
        mostrar_precio: editingPackage.mostrar_precio === true,
        mostrar_stock: editingPackage.mostrar_stock === true,
        imagenes: editingPackage.imagenes?.length ? editingPackage.imagenes : [],
        is_bookable: false,
        requires_time_slot: true,
        custom_duration_minutes: null,
        fulfillment_options: ['pickup'],
      };
      setForm(base);
      setTagsInput(Array.isArray(base.tags) ? base.tags.join(', ') : '');
      loadBookableForPackage(editingPackage.id).then((bookable) => {
        if (bookable) {
          setForm((f) => ({
            ...f,
            is_bookable: bookable.is_bookable,
            requires_time_slot: bookable.requires_time_slot,
            custom_duration_minutes: bookable.custom_duration_minutes,
            fulfillment_options: bookable.fulfillment_options,
          }));
        }
      });
    } else {
      setTagsInput('');
      setPendingImageFiles([]);
      setPendingPreviewUrls((prev) => {
        prev.forEach((url) => URL.revokeObjectURL(url));
        return [];
      });
    }
  }, [editingPackage, loadBookableForPackage]);

  const set = (key: keyof PackageFormData, value: unknown) =>
    setForm((f) => ({ ...f, [key]: value }));

  const filteredProducts = products.filter(
    (p) =>
      !form.items.some((i) => i.product_id === p.id) &&
      p.name.toLowerCase().includes(search.toLowerCase())
  );

  const recientes = recentIds
    .map((id) => products.find((p) => p.id === id))
    .filter(
      (p): p is PackageProduct =>
        !!p && !form.items.some((i) => i.product_id === p.id)
    );

  const productosEnSelector = search.trim()
    ? filteredProducts
    : [
        ...recientes,
        ...products.filter(
          (p) =>
            !form.items.some((i) => i.product_id === p.id) &&
            !recentIds.includes(p.id)
        ),
      ];

  const addProduct = (product: PackageProduct) => {
    const newItem: PackageItem = {
      product_id: product.id,
      quantity: 1,
      unit_cost_override:
        product.inventory_type === 'unlimited' ? product.price : null,
      product,
    };
    set('items', [...form.items, newItem]);
    saveRecentId(product.id);
    setRecentIds(getRecentIds());
    setSearch('');
    setShowProductPicker(false);
  };

  const removeItem = (productId: string) =>
    set('items', form.items.filter((i) => i.product_id !== productId));

  const updateItem = (productId: string, changes: Partial<PackageItem>) =>
    set(
      'items',
      form.items.map((i) =>
        i.product_id === productId ? { ...i, ...changes } : i
      )
    );

  const computedAvailableQty = (): number | null => {
    const tracked = form.items.filter(
      (i) => i.product?.inventory_type === 'tracked'
    );
    if (tracked.length === 0) return null;
    return Math.min(
      ...tracked.map((i) =>
        Math.floor((i.product?.stock_quantity ?? 0) / i.quantity)
      )
    );
  };

  const availableQty = computedAvailableQty();

  const handleSave = async () => {
    if (!form.name.trim()) return;
    if (form.items.length === 0) return;
    const tagsArray = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    let formToSave: PackageFormData = { ...form, tags: tagsArray };
    setSaving(true);
    let ok = false;
    if (editingPackage) {
      ok = await updatePackage(editingPackage.id, formToSave);
    } else {
      const result = await createPackage(formToSave);
      if (result && pendingImageFiles.length > 0) {
        setUploadingImages(true);
        try {
          const urls = await uploadPackageImages(result.id, pendingImageFiles);
          if (urls.length > 0) {
            formToSave = { ...formToSave, imagenes: urls, image_url: urls[0] || formToSave.image_url };
            await updatePackage(result.id, formToSave);
          }
        } finally {
          setUploadingImages(false);
        }
        ok = true;
      } else {
        ok = !!result;
      }
    }
    setSaving(false);
    if (ok) {
      onSaved();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-purple-100 rounded-xl flex items-center justify-center">
              <CubeIcon className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {editingPackage ? 'Editar paquete' : 'Nuevo paquete'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Nombre del paquete *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Ej. Paquete Día de la Madre"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Precio del paquete
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-sm text-gray-400">$</span>
                <input
                  type="number"
                  value={form.price}
                  onChange={(e) => set('price', e.target.value)}
                  placeholder="0.00"
                  min={0}
                  step={0.01}
                  className="w-full border border-gray-200 rounded-xl pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Descripción
            </label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={2}
              placeholder="Describe el paquete..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
            />
          </div>

          {/* Imágenes del paquete */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Imágenes del paquete
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {editingPackage ? (
                (form.imagenes?.length ? form.imagenes : form.image_url ? [form.image_url] : []).map((url, index) => (
                  <div
                    key={`${url}-${index}`}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 ${index === 0 ? 'ring-2 ring-purple-500' : 'border-gray-200'}`}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    {index === 0 && (
                      <div className="absolute top-1 left-1 bg-purple-600 text-white px-1.5 py-0.5 rounded text-xs font-medium flex items-center gap-0.5">
                        <StarIcon className="h-3 w-3" />
                        Principal
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 hover:opacity-100">
                      {index !== 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            const list = form.imagenes?.length ? [...form.imagenes] : form.image_url ? [form.image_url] : [];
                            const [removed] = list.splice(index, 1);
                            if (removed === undefined) return;
                            list.unshift(removed);
                            setForm((f) => ({ ...f, imagenes: list, image_url: list[0] || '' }));
                          }}
                          className="p-1.5 bg-purple-600 text-white rounded-lg"
                          title="Establecer como principal"
                        >
                          <StarIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const list = form.imagenes?.length ? [...form.imagenes] : form.image_url ? [form.image_url] : [];
                          const next = list.filter((_, i) => i !== index);
                          setForm((f) => ({ ...f, imagenes: next, image_url: next[0] || '' }));
                        }}
                        className="p-1.5 bg-red-600 text-white rounded-lg"
                        title="Quitar"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                pendingPreviewUrls.map((url, index) => (
                  <div key={url} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    {index === 0 && (
                      <div className="absolute top-1 left-1 bg-purple-600 text-white px-1.5 py-0.5 rounded text-xs font-medium">
                        Principal
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setPendingImageFiles((prev) => prev.filter((_, i) => i !== index));
                        setPendingPreviewUrls((prev) => {
                          const next = prev.filter((_, i) => i !== index);
                          URL.revokeObjectURL(url);
                          return next;
                        });
                      }}
                      className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded hover:bg-red-700"
                      title="Quitar"
                    >
                      <TrashIcon className="h-3 w-3" />
                    </button>
                  </div>
                ))
              )}
              <label className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-colors">
                {uploadingImages ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-purple-600 border-t-transparent mb-1" />
                    <span className="text-xs text-gray-500">Subiendo...</span>
                  </>
                ) : (
                  <>
                    <PhotoIcon className="h-8 w-8 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500 text-center px-1">Agregar</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  disabled={uploadingImages}
                  onChange={async (e) => {
                    const files = e.target.files;
                    if (!files?.length) return;
                    const fileArray = Array.from(files);
                    if (editingPackage) {
                      setUploadingImages(true);
                      try {
                        const urls = await uploadPackageImages(editingPackage.id, fileArray);
                        const current = form.imagenes?.length ? form.imagenes : form.image_url ? [form.image_url] : [];
                        setForm((f) => ({ ...f, imagenes: [...current, ...urls], image_url: (current[0] || urls[0]) || f.image_url }));
                      } finally {
                        setUploadingImages(false);
                      }
                    } else {
                      setPendingImageFiles((prev) => [...prev, ...fileArray]);
                      setPendingPreviewUrls((prev) => [...prev, ...fileArray.map((f) => URL.createObjectURL(f))]);
                    }
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">La primera imagen es la principal. Puedes agregar varias.</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">
                Componentes del paquete *
              </label>
              {availableQty !== null && (
                <span
                  className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                    availableQty === 0
                      ? 'bg-red-50 text-red-600'
                      : availableQty <= 3
                      ? 'bg-amber-50 text-amber-600'
                      : 'bg-green-50 text-green-600'
                  }`}
                >
                  {availableQty === 0 ? 'Sin stock' : `~${availableQty} disponibles`}
                </span>
              )}
            </div>

            {form.items.length > 0 && (
              <div className="space-y-2 mb-3">
                {form.items.map((item) => {
                  const isUnlimited =
                    item.product?.inventory_type === 'unlimited';
                  const stock = item.product?.stock_quantity ?? 0;
                  const enough = isUnlimited || stock >= item.quantity;
                  return (
                    <div
                      key={item.product_id}
                      className={`flex items-center gap-3 p-3 rounded-xl border ${
                        !enough
                          ? 'border-amber-200 bg-amber-50'
                          : 'border-gray-100 bg-gray-50'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-800 truncate">
                            {item.product?.name}
                          </span>
                          {isUnlimited ? (
                            <span className="flex items-center gap-1 text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full shrink-0">
                              ∞ ilimitado
                            </span>
                          ) : (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
                                enough
                                  ? 'bg-gray-200 text-gray-500'
                                  : 'bg-amber-200 text-amber-700'
                              }`}
                            >
                              {!enough && (
                                <ExclamationTriangleIcon className="inline w-3 h-3 mr-1" />
                              )}
                              stock: {stock}
                            </span>
                          )}
                        </div>
                        {isUnlimited && (
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-xs text-gray-400">Costo:</span>
                            <span className="text-xs text-gray-400">$</span>
                            <input
                              type="number"
                              value={item.unit_cost_override ?? ''}
                              onChange={(e) =>
                                updateItem(item.product_id, {
                                  unit_cost_override: e.target.value
                                    ? parseFloat(e.target.value)
                                    : null,
                                })
                              }
                              placeholder="0.00"
                              min={0}
                              step={0.01}
                              className="w-20 text-xs border border-blue-200 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                            <span className="text-xs text-gray-400">/ unidad</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() =>
                            item.quantity > 1 &&
                            updateItem(item.product_id, {
                              quantity: item.quantity - 1,
                            })
                          }
                          className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100"
                        >
                          <ChevronDownIcon className="w-3 h-3" />
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) =>
                            updateItem(item.product_id, {
                              quantity: Math.max(
                                0.001,
                                parseFloat(e.target.value) || 1
                              ),
                            })
                          }
                          min={0.001}
                          step={1}
                          className="w-12 text-center text-sm border border-gray-200 rounded-lg py-1 focus:outline-none focus:ring-1 focus:ring-purple-400"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            updateItem(item.product_id, {
                              quantity: item.quantity + 1,
                            })
                          }
                          className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100"
                        >
                          <ChevronUpIcon className="w-3 h-3" />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.product_id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {!showProductPicker ? (
              <button
                type="button"
                onClick={() => {
                  setRecentIds(getRecentIds());
                  setShowProductPicker(true);
                }}
                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl py-3 text-sm text-gray-400 hover:border-purple-300 hover:text-purple-500 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Agregar producto o servicio
              </button>
            ) : (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 p-2 border-b bg-gray-50">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar producto..."
                    className="flex-1 text-sm bg-transparent focus:outline-none"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowProductPicker(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
                <div className="max-h-56 overflow-y-auto">
                  {productosEnSelector.length === 0 ? (
                    <p className="text-center text-sm text-gray-400 py-4">
                      Sin resultados
                    </p>
                  ) : (
                    <>
                      {!search.trim() && recientes.length > 0 && (
                        <div className="px-3 pt-2 pb-1">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                            Usados recientemente
                          </p>
                        </div>
                      )}
                      {productosEnSelector.map((product, idx) => {
                        const esReciente =
                          !search.trim() && recentIds.includes(product.id);
                        const esUltimoReciente =
                          esReciente &&
                          idx === recientes.length - 1 &&
                          productosEnSelector.length > recientes.length;
                        return (
                          <React.Fragment key={product.id}>
                            <button
                              type="button"
                              onClick={() => addProduct(product)}
                              className={`w-full flex items-center justify-between px-3 py-2.5 hover:bg-purple-50 text-left transition-colors ${
                                esReciente ? 'bg-gray-50/60' : ''
                              }`}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                {esReciente && (
                                  <span className="text-gray-400 text-xs shrink-0" aria-hidden>
                                    🕐
                                  </span>
                                )}
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-800 truncate">
                                    {product.name}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {product.inventory_type === 'unlimited'
                                      ? '∞ Ilimitado / Servicio'
                                      : `Stock: ${product.stock_quantity ?? 0}`}
                                  </p>
                                </div>
                              </div>
                              {product.inventory_type === 'unlimited' ? (
                                <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full shrink-0 ml-2">
                                  servicio
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400 shrink-0 ml-2">
                                  ${product.price?.toFixed(2)}
                                </span>
                              )}
                            </button>
                            {esUltimoReciente && (
                              <div className="px-3 pt-2 pb-1 border-t border-gray-100">
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                  Todos los productos
                                </p>
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Catalog Configuration (parity with products) */}
          <div className="border-t pt-5 mt-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Configuración del catálogo online</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.mostrar_en_catalogo}
                  onChange={(e) => set('mostrar_en_catalogo', e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                Mostrar en catálogo público
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.disponible_online}
                  onChange={(e) => set('disponible_online', e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                Disponible para compra online
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.mostrar_precio}
                  onChange={(e) => set('mostrar_precio', e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                Mostrar precio
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.mostrar_stock}
                  onChange={(e) => set('mostrar_stock', e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                Mostrar stock disponible
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.destacado}
                  onChange={(e) => set('destacado', e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                Paquete destacado
              </label>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">Orden en catálogo</label>
                <input
                  type="number"
                  min={0}
                  value={form.orden_catalogo}
                  onChange={(e) => set('orden_catalogo', parseInt(e.target.value, 10) || 0)}
                  className="w-20 border border-gray-200 rounded-lg px-2 py-1 text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">Tiempo de preparación</label>
                <input
                  type="text"
                  value={form.tiempo_preparacion}
                  onChange={(e) => set('tiempo_preparacion', e.target.value)}
                  placeholder="24 horas, Inmediato"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">Ubicación</label>
                <select
                  value={form.ubicacion}
                  onChange={(e) => set('ubicacion', e.target.value)}
                  disabled={ubicacionesLoading}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">{ubicacionesLoading ? 'Cargando...' : 'Sin ubicación'}</option>
                  {ubicaciones.filter((u) => u.activa).map((u) => {
                    const label = [u.nombre, u.direccion, u.ciudad].filter(Boolean).join(' – ') || u.nombre;
                    return <option key={u.id} value={label}>{label}</option>;
                  })}
                </select>
                <p className="mt-0.5">
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); navigate('/configuracion?tab=ubicaciones'); }}
                    className="text-xs text-purple-600 hover:underline"
                  >
                    Gestionar ubicaciones
                  </button>
                </p>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Etiquetas (separadas por comas)</label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="regalo, oferta, navidad"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
              {tagsInput.trim() && (
                <p className="text-xs text-gray-500 mt-1">
                  {tagsInput.split(',').map((t) => t.trim()).filter(Boolean).length} etiqueta
                  {tagsInput.split(',').map((t) => t.trim()).filter(Boolean).length !== 1 ? 's' : ''}:{' '}
                  {tagsInput.split(',').map((t) => t.trim()).filter(Boolean).join(', ')}
                </p>
              )}
            </div>
          </div>

          {/* Scheduling (parity with products) */}
          <div className="border-t pt-5 mt-5">
            <div className="flex items-center gap-2 mb-3">
              <CalendarIcon className="h-5 w-5 text-purple-600" />
              <h3 className="text-sm font-semibold text-gray-900">Configuración de agendamiento</h3>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
              <input
                type="checkbox"
                checked={form.is_bookable}
                onChange={(e) => set('is_bookable', e.target.checked)}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              ¿Requiere agendamiento?
            </label>
            <p className="text-xs text-gray-500 mb-3 ml-6">Los clientes podrán elegir fecha y hora al pedir este paquete.</p>
            {form.is_bookable && (
              <div className="ml-6 space-y-3 border-l-2 border-gray-200 pl-4">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.requires_time_slot}
                    onChange={(e) => set('requires_time_slot', e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  Requiere franja horaria
                </label>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-0.5">Duración (minutos, opcional)</label>
                  <input
                    type="number"
                    min={1}
                    value={form.custom_duration_minutes ?? ''}
                    onChange={(e) => set('custom_duration_minutes', e.target.value ? parseInt(e.target.value, 10) : null)}
                    placeholder="30"
                    className="w-24 border border-gray-200 rounded-lg px-2 py-1 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Opciones de entrega</label>
                  <div className="flex flex-wrap gap-2">
                    {['pickup', 'delivery', 'on-site'].map((opt) => (
                      <label key={opt} className="flex items-center gap-1 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={form.fulfillment_options?.includes(opt)}
                          onChange={(e) => {
                            const current = form.fulfillment_options || [];
                            const next = e.target.checked
                              ? [...current, opt]
                              : current.filter((o) => o !== opt);
                            set('fulfillment_options', next.length ? next : ['pickup']);
                          }}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        {opt === 'pickup' ? 'Recoger' : opt === 'delivery' ? 'Domicilio' : 'En sitio'}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => set('is_active', !form.is_active)}
              className={`relative w-10 h-6 rounded-full transition-colors ${
                form.is_active ? 'bg-purple-500' : 'bg-gray-200'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  form.is_active ? 'translate-x-4' : ''
                }`}
              />
            </button>
            <span className="text-sm text-gray-600">
              {form.is_active ? 'Paquete activo' : 'Paquete inactivo'}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={
              saving || !form.name.trim() || form.items.length === 0
            }
            className="px-5 py-2 bg-purple-600 text-white text-sm font-medium rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving
              ? 'Guardando...'
              : editingPackage
              ? 'Guardar cambios'
              : 'Crear paquete'}
          </button>
        </div>
      </div>
    </div>
  );
}
